import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Search, Plus, Edit, Trash2, UserX, UserCheck,
  RefreshCw, ChevronLeft, ChevronRight, X, Save, AlertCircle, Download,
  ArrowUpDown, ChevronUp, ChevronDown,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

interface List {
  listid: number;
  name?: string;
  listname?: string;
}

interface Subscriber {
  subscriberid: number;
  emailaddress: string;
  subscribedate: number;
  confirmdate: number | null;
  confirmed: number;
  unsubscribed: number;
  [key: string]: any; // custom field columns like field_9, field_12
}

interface CustomField {
  fieldid: number;
  listid: number;
  name: string;
  fieldtype: string;
  required: number;
  defaultvalue: string;
}

type StatusFilter = 'confirmed' | 'unsubscribed' | 'all';
type SortKey = 'email' | 'subscribed' | 'unsubscribed' | 'county_state' | 'st';
type SortDir = 'asc' | 'desc';

interface Props {
  list: List;
  customFields: CustomField[];
  onBack: () => void;
}

const DISPLAY_SIZE    = 50;                          // rows shown per visible page
const BATCH_SIZE      = 250;                         // rows fetched per server request
const PAGES_PER_BATCH = BATCH_SIZE / DISPLAY_SIZE;   // = 5

function SortHeader({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const active = activeKey === sortKey;
  return (
    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-gray-800 ${
          active ? 'text-blue-600' : 'text-gray-500'
        }`}
      >
        {label}
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </th>
  );
}

/** Interspire stores unsubscribed as unix time when known; EmailCraft toggles often write 1. */
function formatUnsubscribedDate(raw: number | null | undefined): string {
  const n = Number(raw ?? 0);
  if (!n || n <= 0) return '—';
  // Values before ~2000-01-01 are almost certainly boolean flags, not timestamps
  if (n < 946_684_800) return 'Unknown';
  try {
    return new Date(n * 1000).toLocaleDateString();
  } catch {
    return 'Unknown';
  }
}

/** Parse CSV/text into unique emails. Detects email/emailaddress header; else first column / whole line. */
function parseEmailsFromCsv(text: string): string[] {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];

  const splitRow = (line: string): string[] => {
    const cols: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if ((ch === ',' || ch === '\t' || ch === ';') && !inQuotes) {
        cols.push(cur.trim());
        cur = '';
      } else {
        cur += ch;
      }
    }
    cols.push(cur.trim());
    return cols.map((c) => c.replace(/^"|"$/g, '').trim());
  };

  const firstCols = splitRow(lines[0]);
  const headerIdx = firstCols.findIndex((c) => {
    const n = c.toLowerCase().replace(/[^a-z]/g, '');
    return n === 'email' || n === 'emailaddress' || n === 'emailaddr';
  });
  const hasHeader = headerIdx >= 0;
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const colIdx = hasHeader ? headerIdx : 0;

  const seen = new Set<string>();
  const emails: string[] = [];
  for (const line of dataLines) {
    const cols = splitRow(line);
    const raw = (cols[colIdx] ?? cols[0] ?? line).trim();
    const email = raw.toLowerCase();
    if (!email.includes('@') || seen.has(email)) continue;
    seen.add(email);
    emails.push(email);
  }
  return emails;
}

const SubscriberManager: React.FC<Props> = ({ list, customFields, onBack }) => {
  const { canAccess } = useAuth();
  const canManage = canAccess('contactsManageSubscribers');

  // --- Windowed buffer state ---
  const [buffer, setBuffer]               = useState<Subscriber[]>([]);
  const [bufferBatch, setBufferBatch]     = useState(-1);       // which batch index is cached
  const [bufferLoading, setBufferLoading] = useState(false);    // loading a new batch from server
  const [currentPage, setCurrentPage]     = useState(1);        // global visible page number
  const [total, setTotal]                 = useState<number | null>(null);
  const [countLoading, setCountLoading]   = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  // Reflects the admin-configured County, State field ID
  const [countyStateFieldId, setCountyStateFieldId] = useState<number>(12);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Generation counter — prevents stale count responses from overwriting a newer search
  const countGenRef = useRef(0);

  // --- Search / filter state ---
  const [searchBy, setSearchBy]         = useState<string>('email');
  const [searchInput, setSearchInput]   = useState('');
  const [activeSearch, setActiveSearch] = useState<{ by: string; value: string }>({ by: 'email', value: '' });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('confirmed');
  const [sortKey, setSortKey] = useState<SortKey>('subscribed');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // --- Modal state ---
  const [modalMode, setModalMode]       = useState<'add' | 'edit' | null>(null);
  const [modalSub, setModalSub]         = useState<Subscriber | null>(null);
  const [modalEmail, setModalEmail]     = useState('');
  const [modalFields, setModalFields]   = useState<Record<number, string>>({});
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError]     = useState<string | null>(null);

  // --- Delete confirmation ---
  const [deleteTarget, setDeleteTarget] = useState<Subscriber | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // --- Batch selection / unsubscribe ---
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [showBulkUnsubConfirm, setShowBulkUnsubConfirm] = useState(false);
  const [bulkUnsubLoading, setBulkUnsubLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // --- CSV Bulk Unsubscribe ---
  const [showCsvUnsub, setShowCsvUnsub] = useState(false);
  const [csvUnsubText, setCsvUnsubText] = useState('');
  const [csvUnsubNote, setCsvUnsubNote] = useState('');
  const [csvUnsubEmails, setCsvUnsubEmails] = useState<string[]>([]);
  const [csvUnsubPreview, setCsvUnsubPreview] = useState<{
    matched: number;
    alreadyUnsubscribed: number;
    notFound: number;
    totalSubmitted: number;
  } | null>(null);
  const [csvUnsubBusy, setCsvUnsubBusy] = useState(false);
  const [csvUnsubError, setCsvUnsubError] = useState<string | null>(null);

  // --- Bulk Resubscribe ---
  const [showBulkResub, setShowBulkResub] = useState(false);
  const [bulkResubClearNote, setBulkResubClearNote] = useState(true);
  const [bulkResubLoading, setBulkResubLoading] = useState(false);

  // --- Derived display values (computed, not state) ---
  const localOffset = ((currentPage - 1) % PAGES_PER_BATCH) * DISPLAY_SIZE;
  const visibleRows = buffer.slice(localOffset, localOffset + DISPLAY_SIZE);
  const totalPages  = total !== null ? Math.ceil(total / DISPLAY_SIZE) : null;
  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every((s) => selectedIds.has(s.subscriberid));
  const someVisibleSelected =
    visibleRows.some((s) => selectedIds.has(s.subscriberid)) && !allVisibleSelected;

  // --- fetchBatch: server round-trip for 250 rows; count only on batch 0 ---
  const fetchBatch = useCallback(async (
    batch: number,
    targetPage: number,
    search: { by: string; value: string },
    status: StatusFilter,
    sort: { key: SortKey; dir: SortDir },
  ) => {
    setBufferLoading(true);
    setError(null);

    const base = `/api/interspire/lists/${list.listid}/contacts`;

    // Build shared params (server uses page=batch+1, limit=BATCH_SIZE for OFFSET)
    const baseParams = new URLSearchParams({
      page: String(batch + 1),
      limit: String(BATCH_SIZE),
      status,
      sort: sort.key,
      sortDir: sort.dir,
    });
    if (search.value) {
      if (search.by === 'email') {
        baseParams.set('q', search.value);
      } else {
        baseParams.set('fieldId', search.by);
        baseParams.set('fieldValue', search.value);
      }
    }

    const dataParams = new URLSearchParams(baseParams);
    dataParams.set('skipCount', 'true');

    // Count fires only on batch 0 (fresh search / filter / initial load)
    const shouldFetchCount = batch === 0;
    let gen = countGenRef.current;
    if (shouldFetchCount) {
      gen = ++countGenRef.current;
      setTotal(null);
      setCountLoading(true);
    }

    const countParams = new URLSearchParams(baseParams);
    countParams.set('countOnly', 'true');

    // Fire data and (optionally) count in parallel
    const dataPromise  = axios.get(`${base}?${dataParams}`);
    const countPromise = shouldFetchCount
      ? axios.get(`${base}?${countParams}`)
      : Promise.resolve(null);

    // Render rows as soon as the fast data query returns
    try {
      const { data } = await dataPromise;
      setBuffer(data.subscribers ?? []);
      setBufferBatch(batch);
      setCurrentPage(targetPage);
      if (data.countyStateFieldId != null) setCountyStateFieldId(data.countyStateFieldId);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to load subscribers');
      if (shouldFetchCount) setCountLoading(false);
      setBufferLoading(false);
      return;
    } finally {
      setBufferLoading(false);
    }

    // Fill in the count when it arrives (stale responses ignored via gen)
    if (shouldFetchCount) {
      try {
        const res = await countPromise;
        if (res && countGenRef.current === gen) {
          setTotal(res.data.total ?? 0);
        }
      } catch {
        // Count timed out or failed — pagination shows estimated pages only
        if (countGenRef.current === gen) setTotal(null);
      } finally {
        if (countGenRef.current === gen) setCountLoading(false);
      }
    }
  }, [list.listid]);

  // Trigger batch 0 whenever search, filter, or sort changes; clear selection with query context
  useEffect(() => {
    setSelectedIds(new Set());
    setShowBulkUnsubConfirm(false);
    setSuccessMsg(null);
    fetchBatch(0, 1, activeSearch, statusFilter, { key: sortKey, dir: sortDir });
  }, [fetchBatch, activeSearch, statusFilter, sortKey, sortDir]);

  useEffect(() => {
    setSelectedIds(new Set());
    setShowBulkUnsubConfirm(false);
    setSuccessMsg(null);
  }, [list.listid]);

  // --- Search handlers ---
  const commitSearch = () => {
    const trimmed = searchInput.trim();
    setActiveSearch({ by: searchBy, value: trimmed });
    // useEffect picks up the activeSearch change and fires fetchBatch(0, 1, ...)
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commitSearch();
  };

  const handleSearchByChange = (newBy: string) => {
    setSearchBy(newBy);
    setSearchInput('');
    setActiveSearch({ by: newBy, value: '' });
  };

  const clearSearch = () => {
    setSearchInput('');
    setActiveSearch({ by: searchBy, value: '' });
  };

  // --- Page navigation ---
  const goPage = (p: number) => {
    if (p < 1) return;
    if (totalPages !== null && p > totalPages) return;
    const targetBatch = Math.floor((p - 1) / PAGES_PER_BATCH);
    if (targetBatch === bufferBatch) {
      setCurrentPage(p); // buffer hit — instant, no server request
    } else {
      fetchBatch(targetBatch, p, activeSearch, statusFilter, { key: sortKey, dir: sortDir });
    }
  };

  const refresh = () => {
    const batch = bufferBatch >= 0 ? bufferBatch : 0;
    fetchBatch(batch, currentPage, activeSearch, statusFilter, { key: sortKey, dir: sortDir });
  };

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    // Dates default newest-first; text columns A→Z
    setSortDir(key === 'subscribed' || key === 'unsubscribed' ? 'desc' : 'asc');
  };

  const searchByLabel = searchBy === 'email'
    ? 'Email'
    : customFields.find(cf => cf.fieldid === Number(searchBy))?.name ?? 'Custom Field';

  // --- Add modal ---
  const openAdd = () => {
    setModalMode('add');
    setModalEmail('');
    setModalFields({});
    setModalError(null);
  };

  // --- Edit modal ---
  const openEdit = async (sub: Subscriber) => {
    setModalMode('edit');
    setModalSub(sub);
    setModalEmail(sub.emailaddress);
    setModalError(null);
    setModalLoading(true);
    // Pre-fill from columns already in the buffer row (fast)
    const prefilled: Record<number, string> = {};
    for (const fid of [9, countyStateFieldId]) {
      const colVal = sub[`field_${fid}`];
      if (colVal != null) prefilled[fid] = colVal;
    }
    setModalFields(prefilled);
    setModalLoading(false);

    // Load all fields from API in background
    try {
      const { data } = await axios.get(
        `/api/interspire/lists/${list.listid}/contacts/${sub.subscriberid}/fields`
      );
      setModalFields(data.fields ?? prefilled);
    } catch {
      /* keep prefilled values */
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setModalSub(null);
    setModalError(null);
  };

  // Fields required on Add: anything Interspire marks required, plus County,State always
  const requiredFieldIds = modalMode === 'add'
    ? customFields
        .filter(cf => cf.required === 1 || cf.fieldid === countyStateFieldId)
        .map(cf => cf.fieldid)
    : [];

  const saveModal = async () => {
    if (!modalEmail.trim()) {
      setModalError('Email address is required');
      return;
    }
    if (modalMode === 'add') {
      const missing = requiredFieldIds
        .filter(id => !modalFields[id]?.trim())
        .map(id => customFields.find(cf => cf.fieldid === id)?.name ?? `Field ${id}`);
      if (missing.length > 0) {
        setModalError(`Required: ${missing.join(', ')}`);
        return;
      }
    }
    setModalLoading(true);
    setModalError(null);
    try {
      const fieldsPayload: Record<string, string> = {};
      for (const [k, v] of Object.entries(modalFields)) {
        fieldsPayload[String(k)] = v;
      }

      if (modalMode === 'add') {
        await axios.post(`/api/interspire/lists/${list.listid}/contacts`, {
          emailaddress: modalEmail.trim(),
          fields: Object.keys(fieldsPayload).length > 0 ? fieldsPayload : undefined,
        });
      } else if (modalMode === 'edit' && modalSub) {
        await axios.patch(
          `/api/interspire/lists/${list.listid}/contacts/${modalSub.subscriberid}`,
          {
            emailaddress: modalEmail.trim(),
            fields: Object.keys(fieldsPayload).length > 0 ? fieldsPayload : undefined,
          }
        );
      }
      closeModal();
      // Refresh the current batch to reflect the change
      fetchBatch(bufferBatch >= 0 ? bufferBatch : 0, currentPage, activeSearch, statusFilter, { key: sortKey, dir: sortDir });
    } catch (err: any) {
      setModalError(err.response?.data?.error ?? 'Save failed');
    } finally {
      setModalLoading(false);
    }
  };

  // --- Toggle unsubscribed status (optimistic update on buffer) ---
  const toggleUnsubscribe = async (sub: Subscriber) => {
    const newUnsub = sub.unsubscribed ? 0 : 1;
    setBuffer(prev =>
      prev.map(s => s.subscriberid === sub.subscriberid ? { ...s, unsubscribed: newUnsub } : s)
    );
    try {
      await axios.patch(`/api/interspire/lists/${list.listid}/contacts/${sub.subscriberid}`, {
        unsubscribed: newUnsub === 1,
      });
    } catch {
      // Revert on failure
      setBuffer(prev =>
        prev.map(s => s.subscriberid === sub.subscriberid ? { ...s, unsubscribed: sub.unsubscribed } : s)
      );
    }
  };

  const toggleSelectOne = (subscriberid: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(subscriberid)) next.delete(subscriberid);
      else next.add(subscriberid);
      return next;
    });
  };

  const toggleSelectVisiblePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const s of visibleRows) next.delete(s.subscriberid);
      } else {
        for (const s of visibleRows) next.add(s.subscriberid);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setShowBulkUnsubConfirm(false);
  };

  const confirmBulkUnsubscribe = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (ids.length > 500) {
      setError('You can unsubscribe at most 500 contacts at a time. Clear some selections and try again.');
      setShowBulkUnsubConfirm(false);
      return;
    }
    setBulkUnsubLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await axios.post<{ success: boolean; unsubscribed: number }>(
        `/api/interspire/lists/${list.listid}/contacts/bulk-unsubscribe`,
        { subscriberIds: ids }
      );
      const idSet = new Set(ids);
      const now = Math.floor(Date.now() / 1000);
      setBuffer((prev) =>
        prev.map((s) => (idSet.has(s.subscriberid) ? { ...s, unsubscribed: now } : s))
      );
      setSelectedIds(new Set());
      setShowBulkUnsubConfirm(false);
      setSuccessMsg(
        `Unsubscribed ${res.data.unsubscribed.toLocaleString()} contact${
          res.data.unsubscribed === 1 ? '' : 's'
        }.`
      );
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Bulk unsubscribe failed');
    } finally {
      setBulkUnsubLoading(false);
    }
  };

  const openCsvUnsub = () => {
    setShowCsvUnsub(true);
    setCsvUnsubText('');
    setCsvUnsubNote('');
    setCsvUnsubEmails([]);
    setCsvUnsubPreview(null);
    setCsvUnsubError(null);
  };

  const handleCsvFile = async (file: File | null) => {
    if (!file) return;
    const text = await file.text();
    setCsvUnsubText(text);
    setCsvUnsubPreview(null);
    setCsvUnsubEmails(parseEmailsFromCsv(text));
  };

  const runCsvUnsubPreview = async () => {
    const emails = csvUnsubEmails.length ? csvUnsubEmails : parseEmailsFromCsv(csvUnsubText);
    setCsvUnsubEmails(emails);
    if (!emails.length) {
      setCsvUnsubError('No valid email addresses found in the CSV.');
      return;
    }
    if (!csvUnsubNote.trim()) {
      setCsvUnsubError('Enter a Staff Member note before previewing.');
      return;
    }
    setCsvUnsubBusy(true);
    setCsvUnsubError(null);
    try {
      const res = await axios.post<{
        matched: number;
        alreadyUnsubscribed: number;
        notFound: number;
        totalSubmitted: number;
      }>(`/api/interspire/lists/${list.listid}/contacts/bulk-unsubscribe-preview`, { emails });
      setCsvUnsubPreview(res.data);
    } catch (err: any) {
      setCsvUnsubError(err.response?.data?.error ?? 'Preview failed');
      setCsvUnsubPreview(null);
    } finally {
      setCsvUnsubBusy(false);
    }
  };

  const confirmCsvUnsubscribe = async () => {
    const emails = csvUnsubEmails.length ? csvUnsubEmails : parseEmailsFromCsv(csvUnsubText);
    if (!emails.length || !csvUnsubNote.trim()) return;
    setCsvUnsubBusy(true);
    setCsvUnsubError(null);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await axios.post<{
        unsubscribed: number;
        noteUpdated: number;
        notFound: number;
        alreadyUnsubscribed: number;
      }>(`/api/interspire/lists/${list.listid}/contacts/bulk-unsubscribe-by-email`, {
        emails,
        note: csvUnsubNote.trim(),
      });
      setShowCsvUnsub(false);
      setSuccessMsg(
        `Unsubscribed ${res.data.unsubscribed.toLocaleString()} contact${
          res.data.unsubscribed === 1 ? '' : 's'
        }${
          res.data.notFound
            ? ` · ${res.data.notFound.toLocaleString()} not on this list`
            : ''
        }${
          res.data.alreadyUnsubscribed
            ? ` · ${res.data.alreadyUnsubscribed.toLocaleString()} already unsubscribed`
            : ''
        }.`
      );
      refresh();
    } catch (err: any) {
      setCsvUnsubError(err.response?.data?.error ?? 'Bulk unsubscribe failed');
    } finally {
      setCsvUnsubBusy(false);
    }
  };

  const confirmBulkResubscribe = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkResubLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await axios.post<{ resubscribed: number; notesCleared: number }>(
        `/api/interspire/lists/${list.listid}/contacts/bulk-resubscribe`,
        { subscriberIds: ids, clearStaffMember: bulkResubClearNote }
      );
      setSelectedIds(new Set());
      setShowBulkResub(false);
      setSuccessMsg(
        `Resubscribed ${res.data.resubscribed.toLocaleString()} contact${
          res.data.resubscribed === 1 ? '' : 's'
        }${
          bulkResubClearNote
            ? ` · cleared Staff Member on ${res.data.notesCleared.toLocaleString()}`
            : ''
        }.`
      );
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Bulk resubscribe failed');
    } finally {
      setBulkResubLoading(false);
    }
  };

  // --- Delete ---
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/api/interspire/lists/${list.listid}/contacts/${deleteTarget.subscriberid}`);
      setDeleteTarget(null);
      fetchBatch(bufferBatch >= 0 ? bufferBatch : 0, currentPage, activeSearch, statusFilter, { key: sortKey, dir: sortDir });
    } catch (err: any) {
      alert(err.response?.data?.error ?? 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const listName = list.name ?? list.listname ?? `List ${list.listid}`;

  const handleExport = async () => {
    setExporting(true);
    try {
      const safeName = listName.replace(/[^a-z0-9_-]/gi, '_');
      const params = new URLSearchParams({
        status: statusFilter,
        listName: safeName,
      });
      if (activeSearch.value) {
        if (activeSearch.by === 'email') {
          params.set('q', activeSearch.value);
        } else {
          params.set('fieldId', activeSearch.by);
          params.set('fieldValue', activeSearch.value);
        }
      }
      const res = await axios.get(
        `/api/interspire/lists/${list.listid}/contacts/export?${params}`,
        { responseType: 'blob', timeout: 0 }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}_${statusFilter}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch {
      window.alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Estimated totalPages while count is loading: if a full batch came back, assume more pages exist
  const estimatedTotalPages = totalPages ?? (buffer.length === BATCH_SIZE
    ? Math.floor((bufferBatch + 1) * PAGES_PER_BATCH) + 1
    : (bufferBatch + 1) * PAGES_PER_BATCH);

  return (
    <div className="space-y-4">
      {/* Back + header */}
      <div className="flex items-center space-x-3">
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Lists
        </button>
      </div>

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{listName}</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {total !== null
              ? <>
                  <span className="font-medium">{total.toLocaleString()}</span>
                  {' '}{activeSearch.value ? 'matching' : statusFilter === 'all' ? '' : statusFilter} subscriber{total !== 1 ? 's' : ''}
                </>
              : countLoading
              ? <span className="inline-flex items-center gap-1"><RefreshCw className="h-3 w-3 animate-spin" /> Counting…</span>
              : <span>{activeSearch.value ? 'matching' : statusFilter === 'all' ? '' : statusFilter} subscribers</span>
            }
            {activeSearch.value && (
              <span className="ml-1 text-blue-600">
                · {activeSearch.by === 'email' ? 'email' : searchByLabel} contains "{activeSearch.value}"
              </span>
            )}
          </p>
        </div>
        {canManage && (
          <button
            onClick={openAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add Subscriber
          </button>
        )}
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Search-by field selector */}
        <div className="flex rounded-md border border-gray-300 overflow-hidden shadow-sm min-w-0">
          <span className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 border-r border-gray-300 whitespace-nowrap flex items-center">
            Search by
          </span>
          <select
            value={searchBy}
            onChange={e => handleSearchByChange(e.target.value)}
            className="pl-2 pr-6 py-2 text-sm bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
          >
            <option value="email">Email</option>
            {customFields.map(cf => (
              <option key={cf.fieldid} value={String(cf.fieldid)}>{cf.name}</option>
            ))}
          </select>
        </div>

        {/* Search value input + Search button */}
        <div className="flex gap-2 flex-1 min-w-[200px] max-w-lg">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchBy === 'email' ? 'Search by email…' : `Search ${searchByLabel}… (commas OK)`}
              className="block w-full pl-9 pr-8 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchInput && (
              <button
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={commitSearch}
            className="inline-flex items-center px-3 py-2 border border-blue-500 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <Search className="h-4 w-4 mr-1" />
            Search
          </button>
        </div>

        {/* Status filter */}
        <div className="flex rounded-md border border-gray-300 overflow-hidden text-sm ml-auto">
          {(['confirmed', 'unsubscribed', 'all'] as StatusFilter[]).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 capitalize ${
                statusFilter === s
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {canAccess('contactsExportList') && (
          <button
            onClick={handleExport}
            disabled={exporting || bufferLoading}
            title={
              activeSearch.value
                ? `Export ${statusFilter} subscribers matching current search as CSV`
                : `Export ${statusFilter} subscribers as CSV`
            }
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {exporting
              ? <RefreshCw className="h-4 w-4 animate-spin" />
              : <Download className="h-4 w-4" />}
            Export CSV
          </button>
        )}

        {canManage && (
          <>
            <button
              type="button"
              onClick={openCsvUnsub}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-amber-300 rounded-md text-sm bg-amber-50 text-amber-900 hover:bg-amber-100"
            >
              <UserX className="h-4 w-4" />
              Bulk Unsubscribe
            </button>
            <button
              type="button"
              onClick={() => {
                setBulkResubClearNote(true);
                setShowBulkResub(true);
              }}
              disabled={selectedIds.size === 0}
              title={
                selectedIds.size === 0
                  ? 'Select contacts first'
                  : `Resubscribe ${selectedIds.size} selected`
              }
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-emerald-300 rounded-md text-sm bg-emerald-50 text-emerald-900 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserCheck className="h-4 w-4" />
              Bulk Resubscribe
            </button>
          </>
        )}

        <button
          onClick={refresh}
          disabled={bufferLoading}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm bg-white text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${bufferLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Error / success banners */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center justify-between gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
          <span>{successMsg}</span>
          <button
            type="button"
            onClick={() => setSuccessMsg(null)}
            className="text-green-700 hover:text-green-900"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Batch selection toolbar */}
      {canManage && selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
          <span className="font-medium text-amber-900">
            {selectedIds.size.toLocaleString()} selected
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-amber-800 hover:underline"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setShowBulkUnsubConfirm(true)}
            className="inline-flex items-center gap-1.5 ml-auto px-3 py-1.5 rounded-md text-sm text-white bg-amber-600 hover:bg-amber-700"
          >
            <UserX className="h-4 w-4" />
            Unsubscribe selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {canManage && (
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someVisibleSelected;
                      }}
                      onChange={toggleSelectVisiblePage}
                      disabled={visibleRows.length === 0 || bufferLoading}
                      className="rounded border-gray-300 text-blue-600"
                      title={allVisibleSelected ? 'Deselect page' : 'Select page'}
                      aria-label={allVisibleSelected ? 'Deselect page' : 'Select page'}
                    />
                  </th>
                )}
                <SortHeader label="Email" sortKey="email" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="County, ST" sortKey="county_state" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="ST" sortKey="st" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <SortHeader label="Subscribed" sortKey="subscribed" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Unsubscribed" sortKey="unsubscribed" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                {canManage && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bufferLoading ? (
                <tr>
                  <td colSpan={canManage ? 8 : 6} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Loading…
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 8 : 6} className="px-4 py-8 text-center text-gray-500">
                    No subscribers found
                  </td>
                </tr>
              ) : (
                visibleRows.map(sub => (
                  <tr
                    key={sub.subscriberid}
                    className={
                      selectedIds.has(sub.subscriberid)
                        ? 'bg-blue-50 hover:bg-blue-50/80'
                        : 'hover:bg-gray-50'
                    }
                  >
                    {canManage && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(sub.subscriberid)}
                          onChange={() => toggleSelectOne(sub.subscriberid)}
                          className="rounded border-gray-300 text-blue-600"
                          aria-label={`Select ${sub.emailaddress}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-gray-900">{sub.emailaddress}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{sub.county_state || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{sub.field_9 || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        sub.unsubscribed
                          ? 'bg-red-100 text-red-700'
                          : sub.confirmed
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {sub.unsubscribed ? 'Unsubscribed' : sub.confirmed ? 'Confirmed' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {sub.subscribedate
                        ? new Date(Number(sub.subscribedate) * 1000).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-amber-700">
                      {formatUnsubscribedDate(sub.unsubscribed)}
                    </td>
                    {canManage && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            title={sub.unsubscribed ? 'Resubscribe' : 'Unsubscribe'}
                            onClick={() => toggleUnsubscribe(sub)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          >
                            {sub.unsubscribed
                              ? <UserCheck className="h-4 w-4 text-green-600" />
                              : <UserX className="h-4 w-4 text-yellow-600" />}
                          </button>
                          <button
                            title="Edit"
                            onClick={() => openEdit(sub)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            title="Delete"
                            onClick={() => setDeleteTarget(sub)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {estimatedTotalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-white">
            <p className="text-sm text-gray-700">
              Page <span className="font-medium">{currentPage}</span>
              {total !== null && totalPages !== null
                ? <> of <span className="font-medium">{totalPages}</span> · <span className="font-medium">{total.toLocaleString()}</span> total</>
                : countLoading
                ? <span className="ml-1 inline-flex items-center gap-1 text-gray-400"><RefreshCw className="h-3 w-3 animate-spin" /> counting…</span>
                : null
              }
            </p>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => goPage(currentPage - 1)}
                disabled={currentPage <= 1 || bufferLoading}
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {/* Page number pills — show up to 7, using estimated pages while count loads */}
              {Array.from({ length: Math.min(7, estimatedTotalPages) }, (_, i) => {
                const tp = estimatedTotalPages;
                const mid = Math.min(Math.max(currentPage, 4), Math.max(tp - 3, 4));
                const p = tp <= 7 ? i + 1 : mid - 3 + i;
                if (p < 1 || p > tp) return null;
                return (
                  <button
                    key={p}
                    onClick={() => goPage(p)}
                    disabled={bufferLoading}
                    className={`px-2.5 py-1 rounded border text-sm ${
                      p === currentPage
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => goPage(currentPage + 1)}
                disabled={(totalPages !== null && currentPage >= totalPages) || bufferLoading}
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">

            {/* Header — fixed */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-base font-semibold text-gray-900">
                {modalMode === 'add' ? 'Add Subscriber' : 'Edit Subscriber'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
              {modalError && (
                <div className="flex items-center space-x-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{modalError}</span>
                </div>
              )}

              {/* Email — full width */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={modalEmail}
                  onChange={e => setModalEmail(e.target.value)}
                  className="block w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="email@example.com"
                />
              </div>

              {/* Custom fields — two-column grid */}
              {customFields.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Custom Fields</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    {customFields.map(cf => {
                      const isRequired = requiredFieldIds.includes(cf.fieldid);
                      return (
                        <div key={cf.fieldid}>
                          <label className="block text-xs text-gray-600 mb-0.5">
                            {cf.name}
                            {isRequired && <span className="text-red-500 ml-0.5">*</span>}
                          </label>
                          <input
                            type="text"
                            value={modalFields[cf.fieldid] ?? ''}
                            onChange={e => setModalFields(prev => ({ ...prev, [cf.fieldid]: e.target.value }))}
                            className={`block w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                              isRequired && modalMode === 'add' && !modalFields[cf.fieldid]?.trim()
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-300'
                            }`}
                            placeholder={cf.defaultvalue || cf.name}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer — fixed */}
            <div className="flex justify-end space-x-2 px-5 py-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={closeModal}
                disabled={modalLoading}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveModal}
                disabled={modalLoading}
                className="inline-flex items-center px-3 py-1.5 border border-transparent rounded text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {modalLoading
                  ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <Save className="h-3.5 w-3.5 mr-1.5" />}
                {modalMode === 'add' ? 'Add' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="p-6 space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Delete Subscriber?</h3>
              <p className="text-sm text-gray-600">
                Permanently remove <span className="font-medium">{deleteTarget.emailaddress}</span> from this list?
                This cannot be undone.
              </p>
            </div>
            <div className="flex justify-end space-x-2 px-6 pb-5">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading && <RefreshCw className="h-4 w-4 animate-spin mr-1.5" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk unsubscribe confirmation */}
      {showBulkUnsubConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="p-6 space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Unsubscribe selected?</h3>
              <p className="text-sm text-gray-600">
                Are you sure you want to unsubscribe{' '}
                <span className="font-medium">{selectedIds.size.toLocaleString()}</span> selected
                contact{selectedIds.size === 1 ? '' : 's'} from this list?
              </p>
            </div>
            <div className="flex justify-end space-x-2 px-6 pb-5">
              <button
                type="button"
                onClick={() => setShowBulkUnsubConfirm(false)}
                disabled={bulkUnsubLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmBulkUnsubscribe}
                disabled={bulkUnsubLoading || selectedIds.size === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {bulkUnsubLoading && <RefreshCw className="h-4 w-4 animate-spin mr-1.5" />}
                Unsubscribe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Bulk Unsubscribe dialog */}
      {showCsvUnsub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Bulk Unsubscribe</h3>
              <p className="text-sm text-gray-600">
                Import a CSV of email addresses for <span className="font-medium">{listName}</span> only.
                Matched active contacts will be unsubscribed with today&apos;s date and your Staff Member note.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CSV file or paste</label>
                <input
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  onChange={(e) => void handleCsvFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-gray-600 mb-2"
                />
                <textarea
                  value={csvUnsubText}
                  onChange={(e) => {
                    setCsvUnsubText(e.target.value);
                    setCsvUnsubEmails(parseEmailsFromCsv(e.target.value));
                    setCsvUnsubPreview(null);
                  }}
                  rows={6}
                  placeholder="email&#10;one@example.com&#10;two@example.com"
                  className="block w-full rounded-md border-gray-300 text-sm font-mono shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {(csvUnsubEmails.length
                    ? csvUnsubEmails
                    : parseEmailsFromCsv(csvUnsubText)
                  ).length.toLocaleString()}{' '}
                  unique email{(csvUnsubEmails.length || parseEmailsFromCsv(csvUnsubText).length) === 1 ? '' : 's'} detected
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Staff Member note <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={csvUnsubNote}
                  onChange={(e) => setCsvUnsubNote(e.target.value)}
                  placeholder="e.g. Mid-send pause 2026-09-18 — alumni filter"
                  maxLength={2000}
                  className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              {csvUnsubPreview && (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 space-y-1">
                  <p>
                    <span className="font-medium text-amber-800">
                      {csvUnsubPreview.matched.toLocaleString()}
                    </span>{' '}
                    will be unsubscribed
                  </p>
                  <p>
                    {csvUnsubPreview.alreadyUnsubscribed.toLocaleString()} already unsubscribed
                  </p>
                  <p>{csvUnsubPreview.notFound.toLocaleString()} not found on this list</p>
                </div>
              )}

              {csvUnsubError && (
                <div className="flex items-center gap-2 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {csvUnsubError}
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-6 pb-5">
              <button
                type="button"
                onClick={() => setShowCsvUnsub(false)}
                disabled={csvUnsubBusy}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void runCsvUnsubPreview()}
                disabled={csvUnsubBusy}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-800 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {csvUnsubBusy && !csvUnsubPreview ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-1.5" />
                ) : null}
                Preview counts
              </button>
              <button
                type="button"
                onClick={() => void confirmCsvUnsubscribe()}
                disabled={
                  csvUnsubBusy ||
                  !csvUnsubPreview ||
                  csvUnsubPreview.matched === 0 ||
                  !csvUnsubNote.trim()
                }
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {csvUnsubBusy && csvUnsubPreview ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-1.5" />
                ) : null}
                Confirm unsubscribe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Resubscribe dialog */}
      {showBulkResub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Bulk Resubscribe</h3>
              <p className="text-sm text-gray-600">
                Resubscribe{' '}
                <span className="font-medium">{selectedIds.size.toLocaleString()}</span> selected
                contact{selectedIds.size === 1 ? '' : 's'} on this list? This clears the unsubscribed
                date.
              </p>
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={bulkResubClearNote}
                  onChange={(e) => setBulkResubClearNote(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-blue-600"
                />
                <span>Clear Staff Member field for these contacts</span>
              </label>
            </div>
            <div className="flex justify-end space-x-2 px-6 pb-5">
              <button
                type="button"
                onClick={() => setShowBulkResub(false)}
                disabled={bulkResubLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmBulkResubscribe()}
                disabled={bulkResubLoading || selectedIds.size === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
              >
                {bulkResubLoading && <RefreshCw className="h-4 w-4 animate-spin mr-1.5" />}
                Resubscribe
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriberManager;
